import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Star, Wifi, Wind, UtensilsCrossed, Car, ConciergeBell, Coffee, Sparkles, ShieldCheck, Building2, BedDouble, MapPin, Check, MessageCircle, Users, Cake, Heart, Gem, Briefcase, PartyPopper } from "lucide-react";
import { Reveal, Marquee } from "./primitives";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { MANIFESTO, PHOTO_STRIP, ROOMS, SUITE, CAFE, MEAL_PLANS, TARIFF_NOTES, ROOFTOP_IMAGES, RESTAURANT_STRIP, ROOFTOP_BRANDS, BANQUET_IMAGES, EVENT_PACKAGES, BANQUET_INCLUDES, AMENITIES, REVIEWS, OTAS, GOOGLE_REVIEWS_URL, WHY_DIRECT, FAQS, NEARBY, GALLERY, HOTEL } from "../../data";

const PKG_ICONS = { Cake, Heart, Gem, Briefcase };

const ICONS = { Wifi, Wind, UtensilsCrossed, Car, ConciergeBell, Coffee, Sparkles, ShieldCheck, Building2, BedDouble };
const inr = (n) => `₹${n.toLocaleString("en-IN")}`;
export const waLink = (msg) => `https://wa.me/${HOTEL.whatsapp}?text=${encodeURIComponent(msg)}`;

const SectionLabel = ({ children }) => (
  <span className="overline text-[#B89965] flex items-center gap-3">
    <span className="h-px w-8 bg-[#B89965]" />{children}
  </span>
);

/* ---------- Manifesto ---------- */
export const Manifesto = () => (
  <section id="about" className="bg-[#F5F2ED] py-24 md:py-40">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
      <Reveal><SectionLabel>The Virasat Ethos</SectionLabel></Reveal>
      <Reveal delay={0.1}>
        <h2 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-tight max-w-4xl mt-6">
          A boutique hotel in Patna where heritage, comfort and calm keep quiet company.
        </h2>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-8 max-w-3xl text-lg md:text-xl text-[#1A1A1A]/75 leading-relaxed" data-testid="about-writeup">
          Located opposite Medanta Super Speciality Hospital in a serene residential area of Patna, Hotel Virasat Retreat offers a peaceful escape from the city&rsquo;s hustle and bustle. As a family-owned boutique hotel, we pride ourselves on delivering exceptional hospitality and personalized service to ensure every guest feels at home.
        </p>
      </Reveal>
      <Reveal delay={0.2}>
        <div className="mt-10 max-w-3xl grid gap-6 text-[#1A1A1A]/75 leading-relaxed" data-testid="about-seo-copy">
          <p>
            Our 16 boutique rooms in Kankarbagh, Patna are spread across four thoughtfully designed categories — AC Standard, AC Deluxe, AC Super Deluxe and AC Premium — plus a spacious Residence Suite with its own drawing room, private dining and small kitchen. Every room is fully air-conditioned with a smart TV, free high-speed Wi-Fi, daily housekeeping, complimentary drinking water and 24×7 room service, making us a comfortable choice for business travellers, families, and attendants staying near Medanta Hospital.
          </p>
          <p>
            Dine above the city at <strong>ROOFTOPz Botanical Restaurant</strong>, our fine-dining, family restaurant in Kankarbagh, Patna — kids and elderly friendly, with soft music and multi-cuisine Indian, Chinese and Italian dishes (veg and non-veg). Enjoy signature <strong>Champaran-style dum biryani</strong> at Virasat-e-Biryani and <strong>Fire n Fury Firezza pizzas</strong> from our in-house brand. For a lighter bite, drop into <strong>Tea Toast — Tea Bar &amp; Café</strong>, open 7:00 AM – 10:15 PM with authentic tandoori tea, sandwiches and Chinese fast food. We also offer free home delivery within 3 km of the hotel through WhatsApp.
          </p>
          <p>
            Planning a celebration? Our <strong>3rd-floor banquet hall in Kankarbagh, Patna</strong> is ideal for weddings, receptions, birthday parties, 25th and 75th anniversaries, kitty parties and corporate events — with fully customised event packages including theme decoration, balloon and LED lighting, marquee name lights and in-house catering. Book direct on this website or on WhatsApp and save 10% on stays of two nights or more.
          </p>
        </div>
      </Reveal>
      <div className="mt-20 grid md:grid-cols-3 gap-x-10 gap-y-16">
        {MANIFESTO.map((m, i) => (
          <Reveal key={m.no} delay={i * 0.12} className={i === 1 ? "md:mt-16" : i === 2 ? "md:mt-32" : ""}>
            <p className="font-display text-6xl text-[#B89965]">{m.no}</p>
            <h3 className="font-display text-2xl md:text-3xl mt-4 text-[#2C402E]">{m.title}</h3>
            <p className="mt-4 text-[#1A1A1A]/70 leading-relaxed">{m.body}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Horizontal Photo Strip ---------- */
export const PhotoStrip = () => (
  <section className="bg-[#F5F2ED] py-16 md:py-24 overflow-hidden" data-testid="photo-strip">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 flex items-end justify-between gap-6">
      <Reveal>
        <SectionLabel>A First Look</SectionLabel>
        <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 text-[#2C402E]">Swipe through the retreat.</h2>
      </Reveal>
      <Reveal delay={0.1}><span className="hidden md:block overline text-[#1A1A1A]/40 pb-2">Scroll →</span></Reveal>
    </div>
    <div className="mt-10 flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory no-scrollbar px-5 md:px-10 pb-4 touch-pan-x" data-lenis-prevent>
      {PHOTO_STRIP.map((p, i) => (
        <Reveal
          key={i}
          delay={i * 0.06}
          className="snap-center shrink-0 w-[82vw] sm:w-[60vw] lg:w-[46vw]"
        >
          <div className="clip-img aspect-[16/10]">
            <img src={p.url} alt={p.alt} className="h-full w-full object-cover" loading="lazy" />
          </div>
          <p className="mt-3 font-display text-xl text-[#2C402E]">{p.cap}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------- Direct Booking Offer Banner ---------- */
export const OfferBanner = () => (
  <section data-testid="offer-banner" className="bg-[#B89965] text-[#0D120E]">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-6 md:py-7 flex flex-col md:flex-row items-center justify-between gap-5 text-center md:text-left">
      <div className="flex items-center gap-4">
        <PartyPopper size={34} className="hidden sm:block shrink-0" />
        <div>
          <p className="font-display text-2xl md:text-3xl leading-tight">Book Direct &amp; Save 10%</p>
          <p className="text-sm md:text-base text-[#0D120E]/75 mt-0.5">Skip the travel sites — reserve with us and get 10% off our best room rate. Use code <span className="font-semibold">DIRECT10</span>.</p>
        </div>
      </div>
      <a
        href={waLink("Hi Hotel Virasat Retreat, I'd like to BOOK DIRECT and claim the 10% discount (code DIRECT10). Please share availability.")}
        target="_blank" rel="noopener noreferrer"
        data-testid="offer-whatsapp"
        className="shrink-0 bg-[#0D120E] text-[#F5F2ED] rounded-full px-7 py-3.5 text-sm inline-flex items-center gap-2 hover:bg-[#2C402E] transition-colors"
      >
        <MessageCircle size={16} /> Claim 10% on WhatsApp
      </a>
    </div>
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 pb-4">
      <p className="text-[11px] md:text-xs text-[#0D120E]/60 text-center md:text-left" data-testid="offer-tandc">
        *T&amp;C: Offer valid only on direct bookings of a minimum 2-night stay. Cannot be combined with any other offer or promotion. Subject to availability.
      </p>
    </div>
  </section>
);

/* ---------- Rooms ---------- */
export const Rooms = () => (
  <section id="rooms" className="bg-[#EBE7DF] py-24 md:py-36 border-y border-black/10">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <Reveal><SectionLabel>16 Boutique Rooms</SectionLabel></Reveal>
          <Reveal delay={0.1}><h2 className="font-display text-4xl md:text-6xl tracking-tight mt-5">Rooms in Patna,<br />designed for rest.</h2></Reveal>
        </div>
        <Reveal delay={0.2}><p className="max-w-sm text-[#1A1A1A]/70 leading-relaxed">Four air-conditioned room categories — from Standard to Premium — each with free Wi-Fi, smart TV and daily housekeeping, minutes from Medanta Hospital.</p></Reveal>
      </div>

      <div className="mt-16 grid md:grid-cols-3 gap-6">
        {ROOMS.map((room, i) => (
          <Reveal key={room.id} delay={i * 0.08}>
            <article data-testid={`room-card-${room.id}`} className="group bg-[#F5F2ED] border border-black/10 h-full flex flex-col">
              <div className="clip-img aspect-[4/3] relative">
                <img src={room.image} alt={`${room.name} room — ${HOTEL.name}, Patna`} className="h-full w-full object-cover" loading="lazy" />
                <span className="absolute top-3 left-3 text-[10px] uppercase tracking-wider bg-[#2C402E] text-[#F5F2ED] px-2.5 py-1 rounded-full">
                  {room.ac ? "AC" : "Non-AC"} · {room.count} {room.count > 1 ? "rooms" : "room"}
                </span>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="font-display text-2xl text-[#2C402E]">{room.name}</h3>
                  <span className="text-xs text-[#1A1A1A]/50 whitespace-nowrap flex items-center gap-1"><Users size={13} />{room.occupancy}</span>
                </div>
                <p className="mt-3 text-sm text-[#1A1A1A]/70 leading-relaxed flex-1">{room.blurb}</p>
                <ul className="mt-4 flex flex-wrap gap-2">
                  {room.amenities.slice(0, 4).map((a) => (
                    <li key={a} className="text-[11px] uppercase tracking-wider border border-black/15 px-2.5 py-1 rounded-full text-[#1A1A1A]/70">{a}</li>
                  ))}
                </ul>
                <div className="mt-6 pt-5 border-t border-black/10">
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-[11px] uppercase tracking-wider text-[#1A1A1A]/50">From (Room only)</p>
                      <p className="font-display text-3xl text-[#1A1A1A]">{inr(room.plans.ep)}<span className="text-sm text-[#1A1A1A]/50"> / night</span></p>
                      <p className="text-[11px] text-[#1A1A1A]/45 mt-1">+ 5% GST · meal plans available</p>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <a
                      href={waLink(`Hi Hotel Virasat Retreat, I'd like to book the ${room.name} room (from ${inr(room.plans.ep)}/night). Please share availability.`)}
                      target="_blank" rel="noopener noreferrer"
                      data-testid={`room-whatsapp-${room.id}`}
                      className="flex-1 bg-[#2C402E] text-[#F5F2ED] rounded-full px-4 py-2.5 text-sm inline-flex items-center justify-center gap-1.5 hover:bg-[#1f2e21] transition-colors"
                    >
                      <MessageCircle size={15} /> Book on WhatsApp
                    </a>
                    <a href="#enquiry" data-testid={`room-book-${room.id}`} className="btn-fill text-[#2C402E] hover:text-[#F5F2ED] border border-[#2C402E] rounded-full px-4 py-2.5 text-sm inline-flex items-center gap-1.5">
                      Enquire <ArrowUpRight size={15} />
                    </a>
                  </div>
                </div>
              </div>
            </article>
          </Reveal>
        ))}

        {/* Suite category card */}
        <Reveal delay={ROOMS.length * 0.08}>
          <article data-testid="room-card-residence-suite" className="group bg-[#2C402E] text-[#F5F2ED] border border-[#2C402E] h-full flex flex-col">
            <div className="clip-img aspect-[4/3] relative">
              <img src={SUITE.images[0].url} alt="The Residence Suite with private drawing room — Hotel Virasat Retreat, Patna" className="h-full w-full object-cover" loading="lazy" />
              <span className="absolute top-3 left-3 text-[10px] uppercase tracking-wider bg-[#B89965] text-[#0D120E] px-2.5 py-1 rounded-full">
                Suite · Apartment-Style
              </span>
            </div>
            <div className="p-6 flex flex-col flex-1">
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="font-display text-2xl text-[#B89965]">{SUITE.name}</h3>
                <span className="text-xs text-[#F5F2ED]/60 whitespace-nowrap flex items-center gap-1"><Users size={13} />2-room set</span>
              </div>
              <p className="mt-3 text-sm text-[#F5F2ED]/75 leading-relaxed flex-1">Your own private drawing room, dining space and small kitchen — an Airbnb-style suite for families and long stays in Patna.</p>
              <ul className="mt-4 flex flex-wrap gap-2">
                {["Drawing Room", "Private Dining", "Small Kitchen", "AC Bedroom"].map((a) => (
                  <li key={a} className="text-[11px] uppercase tracking-wider border border-[#F5F2ED]/25 px-2.5 py-1 rounded-full text-[#F5F2ED]/75">{a}</li>
                ))}
              </ul>
              <div className="mt-6 pt-5 border-t border-[#F5F2ED]/15">
                <p className="text-[11px] uppercase tracking-wider text-[#F5F2ED]/50">Starting from</p>
                <p className="font-display text-3xl">{inr(SUITE.priceFrom)}<span className="text-sm text-[#F5F2ED]/50"> / room / night</span></p>
                <div className="mt-4 flex gap-2">
                  <a
                    href={waLink(`Hi Hotel Virasat Retreat, I'd like to book The Residence Suite (from ${inr(SUITE.priceFrom)}/night, with drawing room, private dining & kitchen). Please share availability.`)}
                    target="_blank" rel="noopener noreferrer"
                    data-testid="room-whatsapp-residence-suite"
                    className="flex-1 bg-[#B89965] text-[#0D120E] rounded-full px-4 py-2.5 text-sm inline-flex items-center justify-center gap-1.5 hover:bg-[#c9aa76] transition-colors"
                  >
                    <MessageCircle size={15} /> Book on WhatsApp
                  </a>
                  <a href="#suite" data-testid="room-view-residence-suite" className="btn-fill text-[#F5F2ED] hover:text-[#2C402E] border border-[#F5F2ED]/50 rounded-full px-4 py-2.5 text-sm inline-flex items-center gap-1.5">
                    View <ArrowUpRight size={15} />
                  </a>
                </div>
              </div>
            </div>
          </article>
        </Reveal>
      </div>

      {/* Tariff table */}
      <div className="mt-20">
        <Reveal><SectionLabel>Room Tariff Plan</SectionLabel></Reveal>
        <Reveal delay={0.1}><h3 className="font-display text-3xl md:text-4xl mt-4 text-[#2C402E]">Transparent rates for every room in Patna</h3></Reveal>
        <Reveal delay={0.15} className="mt-8 overflow-x-auto border border-black/10">
          <table className="w-full min-w-[640px] text-left border-collapse" data-testid="tariff-table">
            <thead>
              <tr className="bg-[#2C402E] text-[#F5F2ED] font-body">
                <th className="p-4 font-medium text-sm">Category</th>
                <th className="p-4 font-medium text-sm">Room No.</th>
                <th className="p-4 font-medium text-sm">EP</th>
                <th className="p-4 font-medium text-sm">CP</th>
                <th className="p-4 font-medium text-sm">MAP</th>
                <th className="p-4 font-medium text-sm">AP</th>
              </tr>
            </thead>
            <tbody>
              {ROOMS.map((r, i) => (
                <tr key={r.id} className={i % 2 ? "bg-[#EBE7DF]" : "bg-[#F5F2ED]"}>
                  <td className="p-4 font-display text-lg text-[#2C402E]">{r.name}{r.occupancy.includes("Triple") && <span className="block text-[11px] font-body text-[#1A1A1A]/50">Triple occupancy</span>}</td>
                  <td className="p-4 text-sm text-[#1A1A1A]/70">{r.roomNos}</td>
                  <td className="p-4 text-sm">{inr(r.plans.ep)}</td>
                  <td className="p-4 text-sm">{inr(r.plans.cp)}</td>
                  <td className="p-4 text-sm">{inr(r.plans.map)}</td>
                  <td className="p-4 text-sm">{inr(r.plans.ap)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>

        <div className="mt-8 grid md:grid-cols-2 gap-8">
          <Reveal>
            <p className="overline text-[#1A1A1A]/50 mb-4">Meal Plans</p>
            <ul className="space-y-3">
              {MEAL_PLANS.map((m) => (
                <li key={m.code} className="flex gap-3 text-sm">
                  <span className="font-display text-lg text-[#B89965] w-12 shrink-0">{m.code}</span>
                  <span className="text-[#1A1A1A]/70"><span className="text-[#1A1A1A]">{m.name}.</span> {m.desc}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="overline text-[#1A1A1A]/50 mb-4">Good to Know</p>
            <ul className="space-y-2.5">
              {TARIFF_NOTES.map((n) => (
                <li key={n} className="flex items-center gap-3 text-sm text-[#1A1A1A]/75"><Check size={16} className="text-[#B89965] shrink-0" />{n}</li>
              ))}
            </ul>
            <p className="mt-5 font-display text-lg italic text-[#2C402E]">Our rates are very reasonable — every stay is a genuine value.</p>
          </Reveal>
        </div>
      </div>

      <div className="mt-20">
        <Reveal><p className="overline text-[#1A1A1A]/50 mb-6">Every stay includes</p></Reveal>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-y-6 gap-x-4">
          {AMENITIES.map((a, i) => {
            const Icon = ICONS[a.icon] || Sparkles;
            return (
              <Reveal key={a.label} delay={i * 0.04} className="flex items-center gap-3">
                <Icon size={20} className="text-[#B89965]" />
                <span className="text-sm text-[#1A1A1A]/80">{a.label}</span>
              </Reveal>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

/* ---------- The Residence Suite ---------- */
export const Suite = () => (
  <section id="suite" className="bg-[#0D120E] text-[#F5F2ED] py-24 md:py-36 overflow-hidden">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
      <div className="grid md:grid-cols-12 gap-4 md:gap-8 items-end">
        <div className="md:col-span-5">
          <Reveal><span className="overline text-[#B89965] flex items-center gap-3"><span className="h-px w-8 bg-[#B89965]" />Apartment-Style Living</span></Reveal>
          <Reveal delay={0.1}><h2 className="font-display text-5xl md:text-6xl leading-[0.95] mt-6">The Residence<br /><span className="italic text-[#B89965]">Suite</span></h2></Reveal>
          <Reveal delay={0.2}><p className="mt-6 text-[#F5F2ED]/70 leading-relaxed max-w-md">{SUITE.blurb}</p></Reveal>
          <Reveal delay={0.3}>
            <ul className="mt-8 grid grid-cols-1 gap-2.5 max-w-md">
              {SUITE.features.map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm text-[#F5F2ED]/85"><Check size={16} className="text-[#B89965] shrink-0" />{f}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-8 flex items-end gap-6 flex-wrap">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-[#F5F2ED]/50">Starting from</p>
                <p className="font-display text-4xl">{inr(SUITE.priceFrom)}<span className="text-sm text-[#F5F2ED]/50"> / room / night</span></p>
              </div>
            </div>
            <div className="mt-6 flex gap-3 flex-wrap">
              <a
                href={waLink(`Hi Hotel Virasat Retreat, I'd like to book The Residence Suite (from ${inr(SUITE.priceFrom)}/night, with drawing room, private dining & kitchen). Please share availability.`)}
                target="_blank" rel="noopener noreferrer"
                data-testid="suite-whatsapp"
                className="bg-[#B89965] text-[#0D120E] rounded-full px-7 py-3.5 text-sm inline-flex items-center justify-center gap-2 hover:bg-[#c9aa76] transition-colors"
              >
                <MessageCircle size={16} /> Book on WhatsApp
              </a>
              <a href="#enquiry" data-testid="suite-enquire" className="btn-fill border border-[#F5F2ED]/50 text-[#F5F2ED] hover:text-[#0D120E] rounded-full px-7 py-3.5 text-sm inline-flex items-center gap-2">
                Enquire <ArrowUpRight size={15} />
              </a>
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-7 grid grid-cols-2 gap-3 md:gap-4">
          <Reveal className="clip-img col-span-2 aspect-[16/9]"><img src={SUITE.images[0].url} alt={SUITE.images[0].alt} className="h-full w-full object-cover" loading="lazy" /></Reveal>
          <Reveal delay={0.1} className="clip-img aspect-[4/3]"><img src={SUITE.images[2].url} alt={SUITE.images[2].alt} className="h-full w-full object-cover" loading="lazy" /></Reveal>
          <Reveal delay={0.15} className="clip-img aspect-[4/3]"><img src={SUITE.images[1].url} alt={SUITE.images[1].alt} className="h-full w-full object-cover" loading="lazy" /></Reveal>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- ROOFTOPz (dark) ---------- */
export const Rooftopz = () => (
  <section id="rooftopz" className="bg-[#0D120E] text-[#F5F2ED] py-24 md:py-40 overflow-hidden">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
      <div className="grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-5">
          <Reveal><span className="overline text-[#B89965] flex items-center gap-3"><span className="h-px w-8 bg-[#B89965]" />Rooftop Dining</span></Reveal>
          <Reveal delay={0.1}><h2 className="font-display text-5xl md:text-7xl leading-[0.95] mt-6">ROOFTOPz<br /><span className="italic text-[#B89965]">Botanical</span></h2></Reveal>
          <Reveal delay={0.2}><p className="mt-6 text-[#F5F2ED]/70 leading-relaxed max-w-md">A fine-dining, family restaurant in Kankarbagh, Patna — kids and elderly friendly, with soft music and a warm open-air setting. Dine beneath a canopy of greenery and fairy lights on <span className="text-[#F5F2ED]">Indian, Chinese and Italian</span> vegetarian and non-vegetarian dishes as Patna&rsquo;s skyline glows.</p></Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-[#F5F2ED]/80">
              <span>Open · 12:00 PM – 11:00 PM</span>
              <span>Indian · Chinese · Italian · Veg & Non-Veg</span>
              <span>In-room dining 24×7</span>
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-9 flex flex-wrap gap-3">
              <a href="#enquiry" data-testid="rooftopz-reserve" className="btn-fill inline-flex items-center gap-2 border border-[#B89965] text-[#B89965] hover:text-[#0D120E] rounded-full px-8 py-4">Reserve a Table <ArrowUpRight size={16} /></a>
              <a href="https://zomato.onelink.me/xqzv/sqxfnajw" target="_blank" rel="noopener noreferrer" data-testid="rooftopz-zomato" className="inline-flex items-center gap-2 bg-[#B89965] text-[#0D120E] rounded-full px-8 py-4 hover:bg-[#c9aa76] transition-colors">Order on Zomato <ArrowUpRight size={16} /></a>
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              <a href={HOTEL.menuUrl} target="_blank" rel="noopener noreferrer" data-testid="rooftopz-menu" className="inline-flex items-center gap-2 border border-[#F5F2ED]/40 text-[#F5F2ED] rounded-full px-5 py-2.5 text-sm hover:border-[#B89965] hover:text-[#B89965] transition-colors"><UtensilsCrossed size={14} /> View Menu <ArrowUpRight size={14} /></a>
              <a href={HOTEL.firezzaMenuUrl} target="_blank" rel="noopener noreferrer" data-testid="rooftopz-firezza-menu" className="inline-flex items-center gap-2 border border-[#F5F2ED]/40 text-[#F5F2ED] rounded-full px-5 py-2.5 text-sm hover:border-[#B89965] hover:text-[#B89965] transition-colors"><UtensilsCrossed size={14} /> FIREZZA Menu <ArrowUpRight size={14} /></a>
            </div>
          </Reveal>
          <Reveal delay={0.5}>
            <div className="mt-6 border border-[#B89965]/40 bg-[#B89965]/10 p-5 rounded-sm max-w-md" data-testid="restaurant-delivery-cta">
              <p className="overline text-[#B89965] text-[10px]">Home Delivery</p>
              <p className="mt-2 text-[#F5F2ED] font-display text-xl">Free delivery within 3 km of the hotel.</p>
              <p className="text-sm text-[#F5F2ED]/70 mt-2">Order Indian, Chinese, Italian & FIREZZA pizzas straight to your door in Kankarbagh, Patna.</p>
              <a
                href={waLink("Hi ROOFTOPz Botanical, I'd like to order food for home delivery. My address / area is: ___. Please share today's menu.")}
                target="_blank" rel="noopener noreferrer"
                data-testid="restaurant-whatsapp-order"
                className="mt-4 inline-flex items-center gap-2 bg-[#2C402E] text-[#F5F2ED] rounded-full px-6 py-3 text-sm hover:bg-[#1f2e21] transition-colors"
              >
                <MessageCircle size={15} /> Order on WhatsApp · +91 77650 90955
              </a>
            </div>
          </Reveal>
        </div>
        <div className="md:col-span-7 min-w-0">
          <div className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar gap-4 pb-2 touch-pan-x" data-testid="restaurant-scroll" data-lenis-prevent>
            {RESTAURANT_STRIP.map((r, i) => (
              <Reveal key={i} delay={i * 0.06} className="snap-center shrink-0 w-[78vw] sm:w-[52vw] md:w-[38vw]">
                <div className="clip-img aspect-[4/5]"><img src={r.url} alt={r.alt} className="h-full w-full object-cover" loading="lazy" /></div>
              </Reveal>
            ))}
          </div>
          <p className="mt-3 overline text-[#F5F2ED]/40 text-[10px]">Swipe →</p>
        </div>
      </div>

      {/* Signature in-house food brands */}
      <div className="mt-24 pt-16 border-t border-[#F5F2ED]/15">
        <Reveal><span className="overline text-[#B89965]">Signature Kitchens at ROOFTOPz</span></Reveal>
        <Reveal delay={0.1}><h3 className="font-display text-3xl md:text-4xl mt-4">Two in-house brands, one rooftop.</h3></Reveal>
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {ROOFTOP_BRANDS.map((b, i) => (
            <Reveal key={b.name} delay={i * 0.1}>
              <article data-testid={`brand-${i}`} className="h-full flex flex-col sm:flex-row items-center sm:items-start gap-6 border border-[#F5F2ED]/15 p-6 md:p-8 hover:border-[#B89965]/50 transition-colors duration-500">
                <div className="h-28 w-28 shrink-0 rounded-full overflow-hidden bg-[#0D120E] ring-1 ring-[#B89965]/40 flex items-center justify-center">
                  <img src={b.logo} alt={`${b.name} — ${b.tagline}, ROOFTOPz Botanical Restaurant Patna`} className="h-full w-full object-cover" loading="lazy" />
                </div>
                <div className="text-center sm:text-left">
                  <h4 className="font-display text-2xl text-[#B89965]">{b.name}</h4>
                  <p className="overline text-[#F5F2ED]/50 mt-1 text-[10px]">{b.tagline}</p>
                  <p className="mt-3 text-sm text-[#F5F2ED]/75 leading-relaxed">{b.desc}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Tea Toast Café ---------- */
export const Cafe = () => (
  <section id="cafe" className="bg-[#F5F2ED] py-24 md:py-36">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid md:grid-cols-12 gap-10 items-center">
      <div className="md:col-span-6 grid grid-cols-5 gap-4">
        <Reveal className="clip-img col-span-3 aspect-[4/5]"><img src={CAFE.images[0].url} alt={CAFE.images[0].alt} className="h-full w-full object-cover" loading="lazy" /></Reveal>
        <Reveal delay={0.12} className="clip-img col-span-2 aspect-[3/5] mt-10"><img src={CAFE.images[1].url} alt={CAFE.images[1].alt} className="h-full w-full object-cover" loading="lazy" /></Reveal>
      </div>
      <div className="md:col-span-6">
        <Reveal><SectionLabel>Café · Tea Bar</SectionLabel></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display text-5xl md:text-6xl tracking-tight mt-6 text-[#2C402E]">Tea Toast</h2>
          <p className="overline text-[#B89965] mt-3">{CAFE.sub}</p>
        </Reveal>
        <Reveal delay={0.2}><p className="mt-6 text-[#1A1A1A]/70 leading-relaxed max-w-md">{CAFE.desc}</p></Reveal>
        <Reveal delay={0.25}>
          <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <span className="flex items-center gap-2 text-[#1A1A1A]/80"><Coffee size={15} className="text-[#B89965]" />Open {CAFE.hours}</span>
            <span className="flex items-center gap-2 text-[#1A1A1A]/80"><Star size={15} className="text-[#B89965]" />{CAFE.priceFor2}</span>
          </div>
        </Reveal>
        <Reveal delay={0.3} className="mt-6 bg-[#2C402E] text-[#F5F2ED] p-5">
          <p className="overline text-[#B89965] text-[10px]">Signature</p>
          <p className="mt-2 font-display text-xl">{CAFE.signature}</p>
        </Reveal>
        <div className="mt-6 flex flex-wrap gap-2 max-w-lg">
          {CAFE.highlights.map((h, i) => (
            <Reveal key={h} delay={0.3 + i * 0.05}>
              <span className="text-xs border border-black/15 rounded-full px-3 py-1.5 text-[#1A1A1A]/75">{h}</span>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.5}>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href={HOTEL.zomato} target="_blank" rel="noopener noreferrer" data-testid="cafe-zomato" className="bg-[#B89965] text-[#0D120E] rounded-full px-7 py-3.5 text-sm inline-flex items-center gap-2 hover:bg-[#c9aa76] transition-colors">Order on Zomato <ArrowUpRight size={15} /></a>
            <a href={waLink("Hi Tea Toast (Hotel Virasat Retreat), I'd like to order / know more about your café menu.")} target="_blank" rel="noopener noreferrer" data-testid="cafe-whatsapp" className="btn-fill border border-[#2C402E] text-[#2C402E] hover:text-[#F5F2ED] rounded-full px-7 py-3.5 text-sm inline-flex items-center gap-2"><MessageCircle size={15} /> WhatsApp Us</a>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------- Marquee ribbon ---------- */
export const Ribbon = () => (
  <div className="bg-[#B89965] text-[#2C402E] py-5 text-2xl md:text-3xl">
    <Marquee items={["Heritage", "Legacy", "Rooftop Dining", "Luxury", "Patna", "Book Direct & Save"]} />
  </div>
);

/* ---------- Banquet ---------- */
/* ---------- Banquet & Events ---------- */
export const Banquet = () => (
  <section id="banquet" className="bg-[#F5F2ED] py-24 md:py-36">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
      <div className="grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <Reveal><SectionLabel>3rd Floor Banquet · Events</SectionLabel></Reveal>
          <Reveal delay={0.1}><h2 className="font-display text-4xl md:text-6xl tracking-tight mt-6 text-[#2C402E]">Your celebration,<br /><span className="italic text-[#B89965]">beautifully done for you.</span></h2></Reveal>
          <Reveal delay={0.2}><p className="mt-6 text-[#1A1A1A]/70 leading-relaxed max-w-xl">Looking for a <span className="text-[#2C402E] font-medium">banquet hall in Kankarbagh, Patna</span>? From birthdays and anniversaries to weddings, receptions, kitty parties and corporate events — our 3rd-floor banquet turns your moment into a memory. Tell us your theme and budget; we design a <span className="text-[#2C402E] font-medium">fully customised package</span> and handle everything, so you only have to celebrate.</p></Reveal>
        </div>
        <div className="md:col-span-5">
          <Reveal delay={0.2} className="bg-[#2C402E] text-[#F5F2ED] p-7 md:p-8">
            <p className="overline text-[#B89965]">Customised Event Packages</p>
            <p className="mt-3 font-display text-3xl">Tailored to your theme &amp; budget</p>
            <p className="mt-3 text-sm text-[#F5F2ED]/70">Share your date and headcount on WhatsApp and get a same-day custom quote — decor, catering and lights included.</p>
            <a
              href={waLink("Hi Hotel Virasat Retreat, I'd like a CUSTOM EVENT PACKAGE quote for my event at your banquet. Event type: ___, Date: ___, Approx guests: ___.")}
              target="_blank" rel="noopener noreferrer"
              data-testid="banquet-whatsapp"
              className="mt-6 w-full bg-[#B89965] text-[#0D120E] rounded-full px-6 py-3.5 text-sm inline-flex items-center justify-center gap-2 hover:bg-[#c9aa76] transition-colors font-medium"
            >
              <MessageCircle size={16} /> Get a Custom Quote
            </a>
          </Reveal>
        </div>
      </div>

      {/* Real event gallery */}
      <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {BANQUET_IMAGES.map((img, i) => (
          <Reveal key={i} delay={i * 0.08} className={`clip-img ${i === 0 ? "aspect-[3/4]" : i === 1 ? "aspect-[3/4] md:mt-8" : i === 2 ? "aspect-[3/4]" : "aspect-[3/4] md:mt-8"}`}>
            <img src={img} alt={`Decorated banquet event ${i + 1} at Hotel Virasat Retreat, Patna — birthday, anniversary & party setup`} className="h-full w-full object-cover" loading="lazy" />
          </Reveal>
        ))}
      </div>

      {/* Package cards */}
      <div className="mt-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {EVENT_PACKAGES.map((p, i) => {
          const Icon = PKG_ICONS[p.icon] || PartyPopper;
          return (
            <Reveal key={p.name} delay={i * 0.08}>
              <article data-testid={`event-package-${i}`} className="group h-full bg-[#EBE7DF] border border-black/10 p-7 hover:bg-[#2C402E] hover:text-[#F5F2ED] transition-colors duration-500">
                <Icon size={30} className="text-[#B89965]" />
                <h3 className="font-display text-2xl mt-5 text-[#2C402E] group-hover:text-[#F5F2ED] transition-colors">{p.name}</h3>
                <p className="overline text-[10px] mt-2 text-[#B89965]">{p.tag}</p>
                <p className="mt-3 text-sm text-[#1A1A1A]/70 group-hover:text-[#F5F2ED]/75 leading-relaxed transition-colors">{p.desc}</p>
              </article>
            </Reveal>
          );
        })}
      </div>

      {/* Inclusions + CTA */}
      <div className="mt-16 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-8">
          <Reveal><p className="overline text-[#1A1A1A]/50 mb-6">Every package can include</p></Reveal>
          <div className="grid grid-cols-2 gap-y-3.5 gap-x-4 max-w-2xl">
            {BANQUET_INCLUDES.map((t, i) => (
              <Reveal key={t} delay={i * 0.04} className="flex items-center gap-3 text-sm text-[#1A1A1A]/80"><Check size={16} className="text-[#B89965] shrink-0" />{t}</Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.2} className="md:col-span-4">
          <div className="flex flex-col gap-3">
            <a href={waLink("Hi Hotel Virasat Retreat, I'd like to plan an event at your banquet. Please help me with a custom package.")} target="_blank" rel="noopener noreferrer" data-testid="banquet-whatsapp-2" className="bg-[#2C402E] text-[#F5F2ED] rounded-full px-8 py-4 text-sm inline-flex items-center justify-center gap-2 hover:bg-[#1f2e21] transition-colors"><MessageCircle size={16} /> Plan on WhatsApp</a>
            <a href="#enquiry" data-testid="banquet-enquire" className="btn-fill inline-flex items-center justify-center gap-2 border border-[#2C402E] text-[#2C402E] hover:text-[#F5F2ED] rounded-full px-8 py-4 text-sm">Send an Event Enquiry <ArrowUpRight size={16} /></a>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------- Why Book Direct + Reviews ---------- */
export const WhyDirect = () => (
  <section className="bg-[#2C402E] text-[#F5F2ED] py-24 md:py-36">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
      <div className="grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <Reveal><span className="overline text-[#B89965]">Why Book Direct</span></Reveal>
          <Reveal delay={0.1}><h2 className="font-display text-4xl md:text-6xl tracking-tight mt-5 leading-[1]">Skip the middle-man.<br /><span className="italic text-[#B89965]">Book with us.</span></h2></Reveal>
          <Reveal delay={0.2}><p className="mt-6 text-[#F5F2ED]/70 leading-relaxed max-w-md">Reserve directly on this site or by phone and enjoy the lowest rates, flexible cancellation and priority upgrades — no travel-agent commissions.</p></Reveal>
        </div>
        <div className="md:col-span-7 grid sm:grid-cols-2 gap-px bg-[#F5F2ED]/15 border border-[#F5F2ED]/15">
          {WHY_DIRECT.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.08} className="bg-[#2C402E] p-8">
              <h3 className="font-display text-2xl text-[#B89965]">{w.title}</h3>
              <p className="mt-3 text-sm text-[#F5F2ED]/70 leading-relaxed">{w.body}</p>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="mt-24">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <Reveal><p className="overline text-[#B89965]">Loved by guests · 228+ reviews</p></Reveal>
          <Reveal delay={0.1}>
            <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" data-testid="google-reviews-link" className="btn-fill inline-flex items-center gap-2 border border-[#F5F2ED]/40 text-[#F5F2ED] hover:text-[#2C402E] rounded-full px-6 py-3 text-sm">
              <Star size={15} fill="currentColor" className="text-[#B89965]" /> Read our Google reviews <ArrowUpRight size={15} />
            </a>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {REVIEWS.map((r, i) => (
            <Reveal key={r.name} delay={i * 0.1} className="border border-[#F5F2ED]/15 p-7">
              <div className="flex gap-1 text-[#B89965]">{Array.from({ length: r.rating }).map((_, k) => <Star key={k} size={15} fill="currentColor" />)}</div>
              <p className="mt-4 text-[#F5F2ED]/85 leading-relaxed font-display text-lg italic">&ldquo;{r.text}&rdquo;</p>
              <p className="mt-5 text-sm text-[#F5F2ED]/60">{r.name} · <span className="text-[#B89965]">{r.source}</span></p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="text-xs text-[#F5F2ED]/50 uppercase tracking-wider mr-2">Also rated on</span>
            {OTAS.map((o) => (
              <span key={o} data-testid={`ota-badge-${o.toLowerCase().replace(/[^a-z]/g, "")}`} className="text-xs border border-[#F5F2ED]/25 rounded-full px-3.5 py-1.5 text-[#F5F2ED]/80">{o}</span>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------- Gallery ---------- */
export const Gallery = () => (
  <section id="gallery" className="bg-[#F5F2ED] py-24 md:py-36">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10">
      <Reveal><SectionLabel>Gallery</SectionLabel></Reveal>
      <Reveal delay={0.1}><h2 className="font-display text-4xl md:text-6xl tracking-tight mt-5 text-[#2C402E]">A look inside.</h2></Reveal>
      <div className="mt-14 columns-2 md:columns-3 gap-4 [&>*]:mb-4">
        {GALLERY.map((g, i) => (
          <Reveal key={i} delay={(i % 3) * 0.08} className="clip-img break-inside-avoid">
            <img src={g.url} alt={g.alt} className="w-full object-cover" loading="lazy" />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Location ---------- */
export const Location = () => (
  <section id="location" className="bg-[#EBE7DF] py-24 md:py-36 border-y border-black/10">
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid md:grid-cols-2 gap-12">
      <div>
        <Reveal><SectionLabel>Location</SectionLabel></Reveal>
        <Reveal delay={0.1}><h2 className="font-display text-4xl md:text-6xl tracking-tight mt-5 text-[#2C402E] leading-[1]">Right opposite<br />Medanta Hospital.</h2></Reveal>
        <Reveal delay={0.2}><p className="mt-6 flex items-start gap-3 text-[#1A1A1A]/75 max-w-md leading-relaxed"><MapPin className="text-[#B89965] mt-1 shrink-0" size={20} />{HOTEL.addressLine}, {HOTEL.city}, {HOTEL.state} {HOTEL.pincode}</p></Reveal>
        <div className="mt-8 divide-y divide-black/10 max-w-md">
          {NEARBY.map((n, i) => (
            <Reveal key={n.name} delay={0.2 + i * 0.05} className="flex justify-between py-3.5">
              <span className="text-[#1A1A1A]/80">{n.name}</span>
              <span className="text-[#1A1A1A]/50 text-sm">{n.dist}</span>
            </Reveal>
          ))}
        </div>
      </div>
      <Reveal delay={0.2} className="min-h-[380px] border border-black/10 overflow-hidden">
        <iframe
          title="Hotel Virasat Retreat location map"
          src={`https://www.google.com/maps?q=${HOTEL.mapQuery}&output=embed`}
          className="w-full h-full min-h-[380px] grayscale-[0.2]"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </Reveal>
    </div>
  </section>
);

/* ---------- FAQ ---------- */
export const Faq = () => (
  <section id="faq" className="bg-[#F5F2ED] py-24 md:py-36">
    <div className="mx-auto max-w-[1000px] px-5 md:px-10">
      <Reveal><SectionLabel>Good to Know</SectionLabel></Reveal>
      <Reveal delay={0.1}><h2 className="font-display text-4xl md:text-6xl tracking-tight mt-5 text-[#2C402E]">Frequently asked.</h2></Reveal>
      <Reveal delay={0.2}>
        <Accordion type="single" collapsible className="mt-12" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-black/10">
              <AccordionTrigger className="text-left font-display text-xl md:text-2xl text-[#1A1A1A] hover:no-underline py-6">{f.q}</AccordionTrigger>
              <AccordionContent className="text-[#1A1A1A]/70 leading-relaxed text-base pb-6">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);
